/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { Actionable, Modal, Text, View } from '@az/starc-ui';
import styles from './GalleryStyles.module.scss';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useLabels } from '@/hooks/useLabels';
import dynamic from 'next/dynamic';
import ProgressModal from '@/components/AZCustomComponent/ProgressModal';
import { type ReviewType, type ReviewMediaItem } from '../../../interface/types';
import { type ReviewPhotoMap } from '@/features/customReviews/hooks/useProductReviewImages';
import NextImage from '@/components/NextImage';
import { PlayButton } from '@/components/PlayButton/PlayButton';

type GalleryViewProps = {
  isGalleryModalOpen: boolean;
  isReviewDetailsModalOpen: boolean;
  handleGalleryModalOpen: (isOpen: boolean) => void;
  mediaItems: ReviewMediaItem[];
  handleReviewDetailsModalOpen: (isOpen: boolean) => void;
  imageIndex: number;
  setImageIndex: (stateValue: number) => void;
  reviews: ReviewType[] | null | undefined | ReviewType;
  photoMap: ReviewPhotoMap | null | undefined;
};

const LazyReviewMediaDetailsModal = dynamic(
  () =>
    import('../ReviewImageDetailsModal/ReviewMediaDetailsModal').then(
      (mod) => mod.ReviewMediaDetailsModal,
    ),
  {
    loading: () => <ProgressModal noScroll />,
  },
);

const labelMap = {
  allImagesText: 'label_Reviews_AllImages',
  video: 'label_video',
  image: 'label_image',
};

export const GalleryView: React.FC<GalleryViewProps> = ({
  isGalleryModalOpen,
  isReviewDetailsModalOpen,
  handleGalleryModalOpen,
  mediaItems,
  handleReviewDetailsModalOpen,
  imageIndex,
  setImageIndex,
  reviews,
  photoMap,
}) => {
  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up('lg'));

  const labels = useLabels(labelMap);

  return (
    <View>
      <Modal
        onClose={() => {
          handleGalleryModalOpen(false);
        }}
        size={{ s: 'full', m: 'full', l: 'large', xl: 'large' }}
        open={isGalleryModalOpen}
        hasCloseButton={true}
        headerTitle={!isDesktop ? labels.allImagesText : ' '}
        headerColor={isDesktop ? 'secondary' : 'primary'}
        className={styles.galleryViewModal}
      >
        <View.Item>
          <Text className={styles.allImagesText}>{labels.allImagesText}</Text>
        </View.Item>
        <View className={styles.modalImagesContainer} direction={'row'}>
          <View className={styles.modalImages} direction={'row'}>
            {mediaItems.map((media, index) => {
              const thumbnailUrl = media.type === 'photo' ? media.url : media.thumbnailUrl;
              const isVideo = media.type === 'video';
              const mediaTypeLabel = isVideo ? labels.video : labels.image;

              return (
                <Actionable
                  key={`gallery-media-${index}-${thumbnailUrl}`}
                  onClick={() => {
                    setImageIndex(index);
                    handleReviewDetailsModalOpen(true);
                    handleGalleryModalOpen(false);
                  }}
                >
                  <View.Item className={styles.thumbnails}>
                    <NextImage
                      className={styles.images}
                      src={thumbnailUrl}
                      width={130}
                      height={130}
                      alt={media.caption || `${mediaTypeLabel} #${index + 1}`}
                    />
                    {isVideo && (
                      <div className={styles.playButtonOverlay}>
                        <PlayButton size={4.5} />
                      </div>
                    )}
                  </View.Item>
                </Actionable>
              );
            })}
          </View>
        </View>
      </Modal>
      {isReviewDetailsModalOpen && (
        <LazyReviewMediaDetailsModal
          isReviewDetailsModalOpen={isReviewDetailsModalOpen}
          handleReviewDetailsModalOpen={handleReviewDetailsModalOpen}
          mediaItems={mediaItems}
          imageIndex={imageIndex}
          setImageIndex={setImageIndex}
          reviews={reviews}
          photoMap={photoMap}
        />
      )}
    </View>
  );
};
